import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Paper,
  Tab,
  Tabs,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import FormTextField from "../../components/common/form/FormTextField";
import AppButton from "../../components/common/buttons/AppButton";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../components/common/form/CustomTypography";
import StockIssueRequestsTable from "../../components/stock-issue-requests/StockIssueRequestsTable";
import { StockIssueRequestStore } from "../../../stores/StockIssueRequestStore";
import { StockIssueRequestFilters } from "../../../models/StockIssueRequest/StockIssueRequestFilters";
import { StockIssueRequestStatusTab } from "../../../models/StockIssueRequest/StockIssueRequest";
import { listingStatusTabsStyles } from "../../../styles/listingStatusTabsSx";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";

const DEFAULT_FILTERS: StockIssueRequestFilters = {
  page_no: 1,
  limit: 30,
  sort_by: "created_date",
  sort_order: "desc",
};

// Tabs for shipment status filtering
const TABS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  {
    id: "approved",
    label: "Approved",
  },
  {
    id: "rejected",
    label: "Rejected",
  },
];

const parseUrlFilters = (
  searchParams: URLSearchParams,
): StockIssueRequestFilters => ({
  page_no: parseInt(searchParams.get("page_no") || "1", 10),
  limit: parseInt(searchParams.get("limit") || "30", 10),
  sort_by: searchParams.get("sort_by") || "created_date",
  sort_order: searchParams.get("sort_order") || "desc",
  search_text: searchParams.get("search_text") || undefined,
  start_date: searchParams.get("start_date") || undefined,
  end_date: searchParams.get("end_date") || undefined,
  approved: searchParams.get("approved") || undefined,
  rejected: searchParams.get("rejected") || undefined,
  pending_action: searchParams.get("pending_action") || undefined,
  cancelled: searchParams.get("cancelled") || undefined,
  auto_approved: searchParams.get("auto_approved") || undefined,
});

const deriveActiveTab = (
  filters: StockIssueRequestFilters,
): StockIssueRequestStatusTab => {
  if (filters.pending_action === "true") return "pending";
  if (filters.approved === "true") return "approved";
  if (filters.rejected === "true") return "rejected";
  return "all";
};

const applyTabToFilters = (
  prev: StockIssueRequestFilters,
  tab: StockIssueRequestStatusTab,
): StockIssueRequestFilters => {
  const base = {
    ...prev,
    approved: undefined,
    rejected: undefined,
    pending_action: undefined,
    page_no: 1,
  };
  if (tab === "pending") return { ...base, pending_action: "true" };
  if (tab === "approved") return { ...base, approved: "true" };
  if (tab === "rejected") return { ...base, rejected: "true" };
  return base;
};

type DraftDateRange = Pick<StockIssueRequestFilters, "start_date" | "end_date">;

const StockIssueRequestsListing: React.FC = () => {
  const theme = useTheme();
  const { userStore } = useStores();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const store = useRef(new StockIssueRequestStore()).current;

  const initialFilters = parseUrlFilters(new URLSearchParams(location.search));

  const [filters, setFilters] =
    useState<StockIssueRequestFilters>(initialFilters);
  const [draftDateRange, setDraftDateRange] = useState<DraftDateRange>({
    start_date: initialFilters.start_date,
    end_date: initialFilters.end_date,
  });
  const [searchTerm, setSearchTerm] = useState(
    initialFilters.search_text || "",
  );
  const [activeTab, setActiveTab] = useState<StockIssueRequestStatusTab>(
    deriveActiveTab(initialFilters),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [isInitialMount, setIsInitialMount] = useState(true);

  const hasPermission = userStore.hasPermission([
    EmployeeRoles.VIEW_STOCK_ISSUE_REQUESTS,
  ]);

  useEffect(() => {
    if (!hasPermission) return;
    setIsLoading(true);
    store
      .getStockIssueRequests(filters)
      .catch((err: any) =>
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        ),
      )
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, value.toString());
      }
    });
    navigate({ search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, navigate]);

  const handleSearchBar = (searchText: string | undefined) => {
    if (searchText && searchText.length !== 0) {
      setSearchTerm(searchText);
    } else {
      setSearchTerm("");
      setFilters((prev) => ({ ...prev, search_text: "" }));
    }
  };

  const handleSearchSubmit = () => {
    const trimmed = searchTerm.trim();
    setSearchTerm(trimmed);
    setFilters((prev) => ({ ...prev, search_text: trimmed, page_no: 1 }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      const trimmed = searchTerm.trim();
      setSearchTerm(trimmed);
      setFilters((prev) => ({
        ...prev,
        search_text: trimmed,
        page_no: 1,
      }));
    }
  };

  const handleApply = () => {
    const trimmed = searchTerm.trim();
    setSearchTerm(trimmed);
    setFilters((prev) => ({
      ...prev,
      search_text: trimmed,
      start_date: draftDateRange.start_date,
      end_date: draftDateRange.end_date,
      page_no: 1,
    }));
  };

  const resetFilters = () => {
    setSearchTerm("");
    setActiveTab("all");
    setDraftDateRange({
      start_date: undefined,
      end_date: undefined,
    });
    setFilters({ ...DEFAULT_FILTERS });
  };

  const handleTabChange = (
    _: React.SyntheticEvent,
    newTab: StockIssueRequestStatusTab,
  ) => {
    setActiveTab(newTab);
    setFilters((prev) => applyTabToFilters(prev, newTab));
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setFilters((prev) => ({ ...prev, page_no: newPage }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setFilters((prev) => ({ ...prev, limit: newLimit, page_no: 1 }));
  };

  const handleSortChange = (column: string, sortOrder: string) => {
    setFilters((prev) => ({ ...prev, sort_by: column, sort_order: sortOrder }));
  };

  const handleView = (id: number) => {
    navigate(`/orders/stock-issue-request/${id}`);
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 0.5 }}>
      {!hasPermission && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Paper
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
              }}
            >
              <CustomTypography variant="heading3">
                Stock Issue Requests
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                Manage and track stock issue requests
              </CustomTypography>
            </Box>
          </Paper>
          <PermissionBlockLabel />
        </Box>
      )}

      {hasPermission && (
        <Paper
          sx={{
            mt: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              <CustomTypography variant="heading3">
                Stock Issue Requests
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                Manage and track stock issue requests
              </CustomTypography>
            </Box>
          </Box>

          {/* Search & Filters */}
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isSearchExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="heading6">Search</CustomTypography>
              <IconButton size="small">
                {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
              <Grid2 container spacing={2} columns={12} padding={2}>
                <Grid2
                  size={{ xs: 12, sm: 9, md: 11, lg: 11 }}
                  sx={{ padding: 0 }}
                >
                  <FormTextField
                    marginBottom={0}
                    id="stock-issue-request-search"
                    placeholder="Search by Request ID, Order ID"
                    type="search"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(_, value) => handleSearchBar(value)}
                    onKeyDown={(e) => handleKeyDown(e)}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 3, md: 1, lg: 1 }}>
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={handleSearchSubmit}
                    customStyles={{ width: "100%", height: 40 }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <DateRangePickerNew
                    placeholder="Date Range"
                    startDate={
                      draftDateRange.start_date
                        ? dayjs(draftDateRange.start_date)
                        : null
                    }
                    endDate={
                      draftDateRange.end_date
                        ? dayjs(draftDateRange.end_date)
                        : null
                    }
                    onRangeSelect={(range) => {
                      setDraftDateRange({
                        start_date: range.startDate
                          ? range.startDate.format("YYYY-MM-DD")
                          : undefined,
                        end_date: range.endDate
                          ? range.endDate.format("YYYY-MM-DD")
                          : undefined,
                      });
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <AppButton
                      variant="transparentPrimary"
                      label="Clear"
                      onClick={resetFilters}
                      customStyles={{ width: "100%" }}
                    />
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={handleApply}
                      customStyles={{ width: "100%" }}
                    />
                  </Box>
                </Grid2>
              </Grid2>
            </Collapse>
          </Paper>

          {/* Status Tabs */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mt: 3,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={(t) => ({ ...listingStatusTabsStyles(t), mt: 1 })}
            >
              {TABS.map((tab) => (
                <Tab
                  key={tab.id}
                  label={tab.label.toUpperCase()}
                  id={`shipment-tab-${tab.id}`}
                  value={tab.id}
                />
              ))}
            </Tabs>
          </Box>

          {/* Loading */}
          {isLoading && (
            <Container
              sx={{
                minHeight: "40vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          )}

          {/* Content */}
          {!isLoading && (
            <StockIssueRequestsTable
              requests={store.stockIssueRequests}
              limit={filters.limit}
              page_no={filters.page_no}
              total_count={store.total_count}
              total_pages={store.total_pages}
              sort_by={filters.sort_by}
              sort_order={filters.sort_order}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onSortChange={handleSortChange}
              onView={handleView}
            />
          )}
        </Paper>
      )}
    </Grid2>
  );
};

export default observer(StockIssueRequestsListing);
